import InfoOutlineRounded from "@mui/icons-material/InfoOutlineRounded";
import { Box, Chip, Divider, Typography } from "@mui/material";
import { type FC, type KeyboardEvent, type MouseEvent, memo } from "react";
import { useTranslation } from "react-i18next";
import { BADGE, FONT_SIZE, INK, LEVEL_METER, STATUS } from "../../../theme/tokens";
import type { DayItem } from "../../../utils/models";
import { noLocationString } from "../../DayViewGaps/utils";
import { CommonHeader } from "../../UIToolkit/molecules/CommonHeader";
import { SystemReactTinyPopover } from "../../UIToolkit/Popups/SystemReactTinyPopover";
import { CountBadge, formatCount, getStaffingStatus } from "./CountBadge";
import { RoleCountBadges } from "./LocationHeaderRow";
import type { TimeTitle } from "./useWeeklyGapView";
import { weightedActual } from "./useWeeklyGapView";

type RequiredBreakdownProps = {
	dayItem: DayItem;
	title: TimeTitle;
	dayIdx: number;
	groupLabel: string;
};

const ROW_SX = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, py: 0.5 } as const;
const POPOVER_CONTENT_WIDTH = LEVEL_METER.MENU_MAX_WIDTH;
const POPOVER_CONTENT_MAX_HEIGHT = 300;

export const RequiredBreakdownContent: FC<RequiredBreakdownProps> = ({ dayItem, title, dayIdx, groupLabel }) => {
	const { t } = useTranslation();
	const dayTotal = title.dayTotals[dayIdx];
	const { isUnderstaffed, isOverstaffed } = getStaffingStatus(dayTotal.actual, dayTotal.required);
	const showTotalChip = isUnderstaffed || isOverstaffed;

	return (
		<Box sx={{ width: POPOVER_CONTENT_WIDTH }}>
			<CommonHeader title={t("weekly_gap_view.required_breakdown")} showDivider={false} />
			<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, mb: 1 }}>
				{dayItem.date.toFormat("ccc, MMM d")} · {title.label} · {groupLabel}
			</Typography>
			<Divider />
			<Box sx={{ maxHeight: POPOVER_CONTENT_MAX_HEIGHT, overflowY: "auto" }}>
				{title.locations.map((cl) => {
					const dayData = cl.dayData[dayIdx];
					if (dayData.isBlocked) return null;
					return (
						<Box key={cl.locationName} sx={ROW_SX} data-testid={`weekly-required-breakdown-location-${cl.locationName}`}>
							<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.SECONDARY }}>{cl.locationShortName || cl.locationName || noLocationString}</Typography>
							{dayData.roleCounts ? (
								<RoleCountBadges roleCounts={dayData.roleCounts} />
							) : (
								<CountBadge actual={weightedActual(dayData.nurses)} required={dayData.required} />
							)}
						</Box>
					);
				})}
			</Box>
			<Divider />
			<Box sx={ROW_SX} data-testid="weekly-required-breakdown-total">
				<Typography sx={{ fontSize: FONT_SIZE.CAPTION, fontWeight: 600, color: INK.PRIMARY }}>{t("weekly_gap_view.total_required")}</Typography>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
					<CountBadge actual={dayTotal.actual} required={dayTotal.required} />
					{showTotalChip && (
						<Chip
							data-testid="weekly-required-breakdown-total-chip"
							label={`${formatCount(Math.abs(dayTotal.required - dayTotal.actual))} ${isUnderstaffed ? t("weekly_gap_view.understaffed") : t("weekly_gap_view.overstaffed")}`}
							size="small"
							sx={{
								height: BADGE.TAG_CHIP_HEIGHT_SM,
								fontSize: FONT_SIZE.LABEL_XS,
								fontWeight: 600,
								bgcolor: isUnderstaffed ? STATUS.ERROR.light : STATUS.WARNING.light,
								color: isUnderstaffed ? STATUS.ERROR.dark : STATUS.WARNING.dark,
							}}
						/>
					)}
				</Box>
			</Box>
		</Box>
	);
};

export const RequiredBreakdownInfo: FC<RequiredBreakdownProps> = memo(({ dayItem, title, dayIdx, groupLabel }) => {
	const { t } = useTranslation();
	return (
		<SystemReactTinyPopover
			enterDelay={450}
			leaveDelay={150}
			positions={["bottom", "top"]}
			align="start"
			containerStyle={{ zIndex: "1301" }}
			data-testid={`weekly-required-breakdown-popover-${title.label}-${groupLabel}-${dayIdx}`}
			content={<RequiredBreakdownContent dayItem={dayItem} title={title} dayIdx={dayIdx} groupLabel={groupLabel} />}
		>
			<InfoOutlineRounded
				className="required-breakdown-icon"
				data-testid={`weekly-required-breakdown-icon-${title.label}-${groupLabel}-${dayIdx}`}
				tabIndex={0}
				role="button"
				aria-label={t("weekly_gap_view.required_breakdown")}
				sx={{ fontSize: 14, cursor: "help" }}
				// The trigger sits inside TimeTitleRow's clickable row (row toggles expand/collapse on
				// click/Enter/Space) — stop propagation so interacting with the info icon can't also fire that toggle.
				onClick={(e: MouseEvent<SVGSVGElement>) => e.stopPropagation()}
				onKeyDown={(e: KeyboardEvent<SVGSVGElement>) => e.stopPropagation()}
			/>
		</SystemReactTinyPopover>
	);
});
RequiredBreakdownInfo.displayName = "RequiredBreakdownInfo";
